import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {UserAccount} from 'yougile-model'
import {Accounts} from '../../client/accounts'
import {DirectMessages} from '../../client/direct-messages'
import {Sync} from '../../client/sync'
import {withUpdaters} from '../../containers/updaters'
import {AccountUpdater} from '../../containers/updaters/account-updater'
import {SyncUpdater} from '../../containers/updaters/sync-updater'
import {ChatUtils} from '../../extensions/chat-utils'
import {globals} from '../../extensions/globals-base'
import {MailboxItem, MailboxManager} from '../../extensions/mailbox'
import {PageService} from '../../extensions/page-service'
import {ErrorBoundary} from '../common/error-boundary/error-boundary'
import {Dnd, DndButton} from './dnd'
import {ItemProps, Items} from './items'
import {Window, WindowContainer, WindowMain, WindowTitle} from './window'

interface State {
  readonly open: boolean
  readonly dndOpen: boolean
}

export const NotificationCenter = withUpdaters(
  SyncUpdater({filter: '*', debounceTime: 1000, companyType: 'current'}),
  SyncUpdater({filter: '*', debounceTime: 1000, companyType: 'profile'}),
  AccountUpdater('current')
)(
  class extends React.Component<Record<string, void>, State> {
    constructor(props: Record<string, void>) {
      super(props)
      this.state = {open: false, dndOpen: false}
    }

    public static displayName = 'NotificationCenter'

    private toggle = () =>
      this.setState(prev => ({open: !prev.open, dndOpen: false}))

    private toggleDndOpen = () =>
      this.setState(prev => ({dndOpen: !prev.dndOpen}))

    private onItemClick = (item: MailboxItem) => () => {
      if (item.type === 'task') {
        PageService.goToBoard(item.target.id, item.target.companyId)
      } else {
        ChatUtils.openChat(item.target)
      }
      this.toggle()
    }

    private getItemProps = (item: MailboxItem): ItemProps & {key: string} => {
      const props = {
        key: item.target.id,
        title: Lang.msg.newMessages(item.count),
        type: item.type,
        isNew: item.isNew,
        time: Time.readableTimestamp(item.timestamp),
        text: item.target.title,
        onClick: this.onItemClick(item)
      }
      if (item.type === 'personal-chat') {
        const user = DirectMessages.getDirectChatUser(item.target)
        const name = Accounts.getName(user.id)
        props.text = Lang.msg.chatWithUser + ': ' + name
      }
      if (item.type === 'task') {
        props.title = Lang.msg.newTasks(item.count)
      }
      return props
    }

    public override render(): JSX.Element {
      const acc = Accounts.get(globals.currentUserId)
      const dnd = UserAccount.isInDnd(acc)
      const dndUntil = dnd ? acc.dndUntil : 0
      const curMailbox = MailboxManager.get(globals.getCompanyId())
      const box = Sync.getCurrentSandbox()
      const current = curMailbox ? curMailbox.list() : []
      const personal = MailboxManager.get(Sync.profileId)
        .list()
        .filter(item => {
          const user = DirectMessages.getDirectChatUser(item.target)
          return user && box.isUserInCompany(user.id)
        })
      const items = [...current, ...personal]
        .sort((a, b) => a.timestamp - b.timestamp)
        .map(this.getItemProps)
      const bem = Util.bemFrom('notification-center')
      return (
        <ErrorBoundary>
          <WindowContainer onClick={this.toggle} alert={items.length > 0}>
            {this.state.open && (
              <>
                <div className={bem('overlay')} onClick={this.toggle} />
                <Window>
                  <WindowTitle newCount={items.length}>
                    <DndButton dnd={dnd} onClick={this.toggleDndOpen} />
                  </WindowTitle>

                  <WindowMain>
                    {this.state.dndOpen ? (
                      <Dnd dndUntil={dndUntil} onChange={Accounts.setDnd} />
                    ) : (
                      <Items list={items} />
                    )}
                  </WindowMain>
                </Window>
              </>
            )}
          </WindowContainer>
        </ErrorBoundary>
      )
    }
  }
)
